<template>
    <Head title="Checklist Responses Comparison By Location Report" />

    <div class="max-w-7xl mx-auto space-y-6 py-6 px-4 sm:px-6 lg:px-8">
        <!-- Page Header -->
        <div class="flex items-center justify-between">
            <div>
                <Link :href="route('reports.checklists.index')" class="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 mb-2 inline-flex items-center">
                    <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    Back to Reports
                </Link>
                <h1 class="text-3xl font-bold text-gray-900 dark:text-gray-100">Checklist Responses Comparison By Location Report</h1>
                <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    Compare checklist responses across different locations to identify patterns and trends
                </p>
            </div>
        </div>

        <!-- Filters Section -->
        <Card>
            <div class="p-6">
                <h2 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Filters</h2>
                <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Checklist Name *</label>
                        <Select v-model="filters.template_id" :options="templateOptions" placeholder="Select Checklist Name" />
                    </div>
                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Start Date *</label>
                        <input type="date" v-model="filters.start_date" class="block w-full rounded-md shadow-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:border-blue-500 focus:ring-blue-500" />
                    </div>
                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">End Date *</label>
                        <input type="date" v-model="filters.end_date" class="block w-full rounded-md shadow-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:border-blue-500 focus:ring-blue-500" />
                    </div>
                </div>

                <!-- Generate and Export Buttons -->
                <div class="mt-4 flex items-center justify-end gap-2">
                    <Button @click="generateReport" :disabled="loading || !filters.template_id || !filters.start_date || !filters.end_date">
                        <span v-if="!loading">Generate Report</span>
                        <span v-else class="flex items-center">
                            <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Generating...
                        </span>
                    </Button>

                    <Button v-if="reportData && canExport" @click="exportCsv" variant="secondary">
                        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Export CSV
                    </Button>

                    <Button v-if="reportData && canExport" @click="exportPdf" variant="secondary">
                        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Export PDF
                    </Button>
                </div>
            </div>
        </Card>

        <!-- Report Results -->
        <div v-if="reportData">
            <!-- Summary Statistics -->
            <Card>
                <div class="p-6">
                    <h2 class="text-xl font-bold text-gray-900 dark:text-gray-100 mb-6 flex items-center">
                        <svg class="w-6 h-6 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                        Summary Statistics
                    </h2>
                    <div class="grid grid-cols-2 md:grid-cols-4 gap-6">
                        <div class="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                            <div class="text-sm font-medium text-blue-600 dark:text-blue-400 mb-1">Checklists Completed</div>
                            <div class="text-3xl font-bold text-blue-900 dark:text-blue-100">{{ reportData.statistics.total_checklists }}</div>
                        </div>
                        <div class="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
                            <div class="text-sm font-medium text-green-600 dark:text-green-400 mb-1">Unique Locations</div>
                            <div class="text-3xl font-bold text-green-900 dark:text-green-100">{{ reportData.statistics.unique_locations }}</div>
                        </div>
                        <div class="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg border border-yellow-200 dark:border-yellow-800">
                            <div class="text-sm font-medium text-yellow-600 dark:text-yellow-400 mb-1">Unique Assets</div>
                            <div class="text-3xl font-bold text-yellow-900 dark:text-yellow-100">{{ reportData.statistics.unique_assets }}</div>
                        </div>
                        <div class="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg border border-purple-200 dark:border-purple-800">
                            <div class="text-sm font-medium text-purple-600 dark:text-purple-400 mb-1">Users Completed</div>
                            <div class="text-3xl font-bold text-purple-900 dark:text-purple-100">{{ reportData.statistics.unique_users }}</div>
                        </div>
                    </div>
                </div>
            </Card>

            <!-- Field-by-Field Analysis with Location Comparison -->
            <div class="space-y-4">
                <div v-for="(field, index) in reportData.fields" :key="index">
                    <Card>
                        <div class="p-6">
                            <!-- Question Header -->
                            <div class="border-b border-gray-200 dark:border-gray-700 pb-4 mb-4">
                                <div class="flex items-start justify-between">
                                    <div class="flex-1">
                                        <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">{{ field.question }}</h3>
                                        <div class="flex items-center gap-2">
                                            <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                                                {{ formatFieldType(field.field_type) }}
                                            </span>
                                            <span v-if="field.is_dependent" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                                                Dependent Question
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <!-- Dependency Information (for dependent questions) -->
                                <div v-if="field.is_dependent && field.dependency_info" class="mt-3 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                                    <div class="text-sm font-medium text-amber-900 dark:text-amber-100 mb-1">Depends on:</div>
                                    <div class="text-sm text-amber-700 dark:text-amber-300 mb-1">{{ field.dependency_info.parent_question }}</div>
                                    <div class="text-xs text-amber-600 dark:text-amber-400 italic">{{ field.dependency_info.rule }}</div>
                                </div>

                                <!-- Desired Answer (if exists) -->
                                <div v-if="field.desired_answer_display" class="mt-3 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                                    <div class="text-sm font-medium text-purple-900 dark:text-purple-100 mb-1">Desired Answer:</div>
                                    <div class="text-sm text-purple-700 dark:text-purple-300">{{ field.desired_answer_display }}</div>
                                </div>

                                <!-- Pass/Fail Stats (if has desired answer) -->
                                <div v-if="field.has_desired_answer && field.pass_fail_stats" class="mt-3 grid grid-cols-2 gap-3">
                                    <div class="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                                        <div class="text-xs font-medium text-green-600 dark:text-green-400 mb-1">Passed</div>
                                        <div class="flex items-baseline gap-2">
                                            <span class="text-2xl font-bold text-green-900 dark:text-green-100">{{ field.pass_fail_stats.pass_percentage }}%</span>
                                            <span class="text-sm text-green-600 dark:text-green-400">({{ field.pass_fail_stats.pass_count }} of {{ field.pass_fail_stats.total }})</span>
                                        </div>
                                    </div>
                                    <div class="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                                        <div class="text-xs font-medium text-red-600 dark:text-red-400 mb-1">Failed</div>
                                        <div class="flex items-baseline gap-2">
                                            <span class="text-2xl font-bold text-red-900 dark:text-red-100">{{ field.pass_fail_stats.fail_percentage }}%</span>
                                            <span class="text-sm text-red-600 dark:text-red-400">({{ field.pass_fail_stats.fail_count }} of {{ field.pass_fail_stats.total }})</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Location Breakdown Table -->
                            <div v-if="field.location_breakdown && field.location_breakdown.length > 0">
                                <h4 class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Breakdown by Location</h4>

                                <!-- Simple Count Type Fields (text, date, time, photo, file, number, decimal) -->
                                <div v-if="isSimpleCountType(field.field_type)" class="overflow-x-auto">
                                    <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                        <thead class="bg-gray-50 dark:bg-gray-800">
                                            <tr>
                                                <th scope="col" class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    Location
                                                </th>
                                                <th scope="col" class="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    Response Count
                                                </th>
                                                <th scope="col" class="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    Pass %
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody class="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                                            <tr v-for="location in field.location_breakdown" :key="location.location_name">
                                                <td class="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                                                    {{ location.location_name }}
                                                </td>
                                                <td class="px-4 py-3 text-sm text-center text-gray-900 dark:text-gray-100">
                                                    {{ location.response_count }}
                                                </td>
                                                <td class="px-4 py-3 text-sm text-center">
                                                    <span v-if="location.pass_percentage !== undefined" :class="getPassRateColorClass(location.pass_percentage)" class="font-semibold">
                                                        {{ location.pass_percentage }}%
                                                    </span>
                                                    <span v-else class="text-gray-400">N/A</span>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>

                                <!-- Option-based Fields (yes/no, pass/fail/na, multiple_choice, checkboxes, rating) -->
                                <div v-else class="overflow-x-auto">
                                    <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                        <thead class="bg-gray-50 dark:bg-gray-800">
                                            <tr>
                                                <th scope="col" class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    Location
                                                </th>
                                                <th v-for="option in field.field_options" :key="option" scope="col" class="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    {{ option }}
                                                </th>
                                                <th scope="col" class="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    Pass %
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody class="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                                            <tr v-for="location in field.location_breakdown" :key="location.location_name">
                                                <td class="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">
                                                    {{ location.location_name }}
                                                </td>
                                                <td v-for="option in field.field_options" :key="option" class="px-4 py-3 text-sm text-center text-gray-900 dark:text-gray-100">
                                                    {{ getOptionCount(location, option) }}
                                                </td>
                                                <td class="px-4 py-3 text-sm text-center">
                                                    <span v-if="location.pass_percentage !== undefined" :class="getPassRateColorClass(location.pass_percentage)" class="font-semibold">
                                                        {{ location.pass_percentage }}%
                                                    </span>
                                                    <span v-else class="text-gray-400">N/A</span>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </Card>
                </div>
            </div>
        </div>

        <!-- Empty State -->
        <Card v-else-if="!loading">
            <div class="p-12 text-center">
                <svg class="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p class="text-sm text-gray-500 dark:text-gray-400">Select a checklist name and date range, then click "Generate Report" to view location comparison</p>
            </div>
        </Card>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { Head, Link, usePage } from '@inertiajs/vue3';
import Card from '@/Components/Common/Card.vue';
import Button from '@/Components/Common/Button.vue';
import Select from '@/Components/Common/Select.vue';
import route from '@/Utils/route';
import { useToast } from '@/Composables/useToast';
import { useSecureAxios } from '@/Composables/useSecureAxios';

const toast = useToast();
const axios = useSecureAxios();
const page = usePage();

const props = defineProps({
    filterOptions: Object,
    canExport: Boolean,
});

// State
const loading = ref(false);
const reportData = ref(null);
const filters = ref({
    template_id: null,
    start_date: null,
    end_date: null,
});

// Computed
const templateOptions = computed(() => {
    return props.filterOptions.templates.map(t => ({
        value: t.id,
        label: `${t.code} - ${t.name}`
    }));
});

// Methods
const generateReport = async () => {
    loading.value = true;

    try {
        const response = await axios.post(route('reports.checklists.responses-comparison-by-location.generate'), filters.value);

        if (response.data.success) {
            reportData.value = response.data.data;
            toast.success('Report generated successfully');
        }
    } catch (error) {
        toast.error('Failed to generate report');
        console.error(error);
    } finally {
        loading.value = false;
    }
};

const exportPdf = async () => {
    try {
        const response = await axios.post(route('reports.checklists.export-pdf'), {
            report_type: 'responses_comparison_by_location',
            data: reportData.value,
            filters: filters.value,
        }, {
            responseType: 'blob'
        });

        const blob = new Blob([response.data], { type: 'application/pdf' });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.setAttribute('download', `responses-comparison-by-location-${new Date().toISOString().split('T')[0]}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();

        toast.success('PDF exported successfully');
    } catch (error) {
        toast.error('Failed to export PDF');
        console.error(error);
    }
};

const exportCsv = async () => {
    try {
        const response = await axios.post(route('reports.checklists.responses-comparison-by-location.export-csv'), {
            data: reportData.value,
        }, {
            responseType: 'blob'
        });

        const blob = new Blob([response.data], { type: 'text/csv' });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.setAttribute('download', `responses-comparison-by-location-${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();

        toast.success('CSV exported successfully');
    } catch (error) {
        toast.error('Failed to export CSV');
        console.error(error);
    }
};

const formatFieldType = (type) => {
    const types = {
        'yes_no': 'Yes/No',
        'pass_fail_na': 'Pass/Fail/NA',
        'multiple_choice': 'Multiple Choice',
        'checkboxes': 'Checkboxes',
        'checkbox': 'Checkboxes',
        'multi_select': 'Multiple Selection',
        'multiple_selection': 'Multiple Selection',
        'dropdown': 'Dropdown',
        'dropdown_select': 'Dropdown Select',
        'short_text': 'Short Text',
        'long_text': 'Long Text',
        'text_input': 'Text Input',
        'textarea': 'Text Area',
        'number_input': 'Number',
        'decimal_input': 'Decimal',
        'rating': 'Rating Scale',
        'rating_scale': 'Rating Scale',
        'date': 'Date',
        'date_picker': 'Date Picker',
        'time': 'Time',
        'time_picker': 'Time Picker',
        'datetime': 'Date & Time',
        'datetime_picker': 'Date & Time Picker',
        'photo': 'Photo Upload',
        'photo_capture': 'Photo Capture',
        'file': 'File Upload',
        'file_upload': 'File Upload',
        'signature': 'Signature',
    };
    return types[type] || type;
};

const isSimpleCountType = (fieldType) => {
    const simpleTypes = [
        'short_text', 'long_text', 'text_input', 'textarea',
        'date', 'date_picker',
        'time', 'time_picker',
        'photo', 'photo_capture',
        'file', 'file_upload',
        'number_input',
        'decimal_input'
    ];
    return simpleTypes.includes(fieldType);
};

const getOptionCount = (location, option) => {
    if (!location.distribution) {
        return 0;
    }

    const dist = location.distribution.find(d => d.label === option);
    return dist ? dist.count : 0;
};

const getPassRateColorClass = (percentage) => {
    if (percentage >= 80) {
        return 'text-green-600 dark:text-green-400';
    } else if (percentage >= 50) {
        return 'text-yellow-600 dark:text-yellow-400';
    } else {
        return 'text-red-600 dark:text-red-400';
    }
};
</script>
